import type { Translations } from "./types";

export const en: Translations = {
  meta: {
    title: "Your Kimono Experience",
    subtitle: "A step-by-step guide to your perfect day in Asakusa",
  },
  langName: "English",
  steps: {
    booking: {
      title: "Book & Prepare",
      duration: "Before your visit",
      summary:
        "Check our pricing, estimate your total cost, and secure your booking with a simple deposit.",
      details: [
        "View our [pricing](https://price.kimonorentalann.com) for kimono rental, makeup, and photography services.",
        "Use our [Experience Calculator](https://experience.kimonorentalann.com) to estimate the total cost for the services you'd like.",
        "Kimono rental includes hair styling, bag, fan, kimono sandals, and all accessories.",
        "If you'd like photography, a ¥3,000 deposit is required to confirm.",
        "After your deposit, send us the transfer confirmation — we'll send you a booking link.",
        "Fill in the booking form completely. You'll receive a confirmation email with your booking details.",
        "Check our [Find Us](https://find.kimonorentalann.com) page for directions to our shop before your visit.",
      ],
      tips: [
        "Kimono rental already includes hair styling and accessories — no extra cost!",
      ],
    },
    dressing: {
      title: "Kimono Selection & Dressing",
      duration: "20–30 min",
      summary:
        "Choose your kimono, get dressed by our staff, and pick your hairstyle from 40+ options.",
      details: [
        "Show your booking code and name to our staff when you arrive.",
        "Coming as a group? Children, men and anyone not getting dressed can wait comfortably on 3F (same building, our nail salon floor) while the others get ready — the 4F room gets crowded with many people.",
        "If you've pre-selected a kimono tier, let us know (e.g., \"I'd like the ¥7,000 kimono\").",
        "Browse and choose your kimono in person, or view options on our iPad.",
        "Change into kimono undergarments — you'll need to remove bulky clothing like skirts, pants, or high-collar tops.",
        "Your belongings go in a numbered bag — please remember your bag number for easy retrieval.",
        "Our staff will dress you in kimono. It's tied with cords (no buttons), so it may feel snug — this is normal and keeps the kimono looking beautiful.",
        "Choose your hairstyle from over 40 styles available at the shop.",
        "Men's kimono dressing takes about 5–10 minutes.",
      ],
      tips: [
        "The snug feeling is intentional — loose ties cause the kimono to look untidy.",
        "Carry your valuables (cash, passport, phone) with you when you go out. We can only store clothing and non-valuable items.",
      ],
    },
    makeup: {
      title: "Makeup",
      duration: "30–40 min",
      badge: "Optional",
      summary:
        "Professional makeup with tones specially matched for kimono photography.",
      details: [
        "Our makeup artists apply tones that complement kimono and photograph beautifully.",
        "Check our photo gallery for examples of kimono looks with makeup.",
      ],
      tips: [
        "Browse our Instagram or website photos to get an idea of the makeup style before your visit.",
      ],
    },
    photography: {
      title: "Photoshoot",
      duration: "30–60 min",
      badge: "Optional",
      summary:
        "Our photographer guides you to the best spots around Sensoji Temple in Asakusa.",
      details: [
        "Your photographer will take you to beautiful locations around Sensoji Temple and the Asakusa area.",
        "Preview our photo spots at [map.kimonorentalann.com](https://map.kimonorentalann.com) to see where we usually shoot.",
        "Session length depends on your package — 30 to 60 minutes.",
        "Want more time? You can add an extra 30 or 60 minutes.",
        "After the shoot, you're free to explore Asakusa in your kimono until 6:00 PM.",
      ],
    },
    returnKimono: {
      title: "Return Your Kimono",
      duration: "By 6:00 PM",
      summary:
        "Come back to the shop and return your kimono by 6:00 PM.",
      details: [
        "Please return to the shop by 6:00 PM.",
        "Need more time? Let us know in advance so we can arrange staff to wait for you.",
      ],
    },
    receivePhotos: {
      title: "Receive Your Photos",
      duration: "After your shoot",
      summary:
        "Get your photos — either transferred to your phone same-day, or professionally edited within 7–10 days.",
      details: [],
    },
  },
  photoToggle: {
    fileDelivery: "File Delivery",
    editedPackage: "Edited Package",
    fileDeliveryDetails: [
      "We transfer photos directly from the SD card to your phone using a cable adapter.",
      "Works with both Android and iOS devices.",
      "Each set of photos is about 1–3 GB — please make sure you have enough storage.",
      "If your phone doesn't have enough space, we'll upload to Google Drive and send you the link via Facebook, WhatsApp, or WeChat.",
    ],
    editedPackageDetails: [
      "We'll create a group chat on WhatsApp or Facebook Messenger with you and the photographer.",
      "Select your favorite photos by sending us the photo codes (e.g., 1134, 2234). Please send codes, not screenshots — we take many similar shots and need the exact number.",
      "Editing takes 7–10 days. We'll share the finished photos via Google Drive in your group chat.",
      "Photos are stored on Google Drive for about 1 month — please download them before they expire.",
    ],
  },
  cta: {
    checkPricing: "View Pricing",
    calculateCost: "Estimate Cost",
  },
  common: {
    tip: "Tip",
    optional: "Optional",
    stepOf: "of",
    expandToRead: "Tap to read more",
  },
};
